import { useEffect, useState } from 'react';
import { PLANS, PLAN_FEATURES, PLAN_FREE, PLAN_PRO } from '@shared/plans.js';
import { api, ApiError, type BillingStatus, type User } from '@/lib/api';
import { isNative, platform } from '@/lib/native';
import { Alert, Modal } from './kit';
import { IconCheck, IconClose } from './icons';

/**
 * The upgrade screen.
 *
 * Two things it deliberately does not do. It never appears out of nowhere —
 * something has to be locked for it to open, and `reason` says what. And it
 * never claims a price the server has not been configured to charge: with no
 * payment keys set the buy button is replaced by an honest note rather than a
 * button that fails.
 */
export default function UpgradeModal({
  user, reason, onClose,
}: {
  user: User;
  /** What the person just ran into. Shown at the top so the modal has a reason to exist. */
  reason?: string | null;
  onClose: () => void;
}) {
  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    api.billingStatus()
      .then((s) => { if (live) setStatus(s); })
      .catch(() => { if (live) setError('Could not load plan details.'); });
    return () => { live = false; };
  }, []);

  const isPro = status ? status.plan === PLAN_PRO : user.plan === PLAN_PRO;

  const upgrade = () => {
    setBusy(true);
    setError('');
    api.startCheckout()
      .then(({ url }) => { window.location.href = url; })
      .catch((err) => {
        setError(err instanceof ApiError ? err.message : 'Could not start the upgrade.');
        setBusy(false);
      });
  };

  const manage = () => {
    setBusy(true);
    api.billingPortal()
      .then(({ url }) => { window.location.href = url; })
      .catch((err) => {
        setError(err instanceof ApiError ? err.message : 'Could not open your billing page.');
        setBusy(false);
      });
  };

  /**
   * Formatted for the reader, from the figures Stripe gave us. Doing it here
   * rather than on the server means someone in Berlin sees 19,99 £ and someone
   * in London sees £19.99, from the same number.
   */
  const price = status?.price
    ? new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: status.price.currency,
      // Whole amounts read better without the trailing zeroes.
      minimumFractionDigits: status.price.amount % 100 === 0 ? 0 : 2,
    }).format(status.price.amount / 100)
    : null;

  const every = status?.price
    ? (status.price.intervalCount > 1
      ? `every ${status.price.intervalCount} ${status.price.interval}s`
      : `a ${status.price.interval}`)
    : 'a year';

  const renews = status?.planUntil
    ? new Date(status.planUntil).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  return (
    <Modal title={isPro ? 'Ink Share Pro' : 'Go Pro'} onClose={onClose} wide>
      {reason ? <Alert tone="info">{reason}</Alert> : null}
      {error ? <Alert tone="error">{error}</Alert> : null}

      {isPro ? (
        <>
          <p style={{ marginTop: 0 }}>
            You are on Pro
            {renews ? (status?.renewing ? `, renewing on ${renews}.` : `, until ${renews}.`) : '.'}
          </p>
          {status?.source === 'play' ? (
            <p className="tiny muted">
              This subscription was bought through Google Play. Manage or cancel it in the
              Play Store under Payments and subscriptions.
            </p>
          ) : (
            <button type="button" className="btn btn--ghost btn--block" onClick={manage} disabled={busy}>
              Manage or cancel
            </button>
          )}
        </>
      ) : null}

      <div className="plans">
        <PlanCard
          plan={PLANS[PLAN_FREE]}
          current={!isPro}
          features={PLAN_FEATURES.map((f) => ({ label: f.free, has: f.free !== '—' }))}
        />
        <PlanCard
          plan={PLANS[PLAN_PRO]}
          highlight
          current={isPro}
          price={price ? `${price} ${every}` : null}
          features={PLAN_FEATURES.map((f) => ({ label: f.pro, has: f.pro !== '—' }))}
        />
      </div>

      {!isPro ? (
        <>
          {status && !status.available ? (
            <Alert tone="info">
              Upgrades are not switched on for this server yet.
            </Alert>
          ) : isNative() && platform() === 'android' ? (
            // Play's rules require its own billing for anything bought inside
            // the app, so the web is the only honest route until the native
            // half is wired up.
            <Alert tone="info">
              To go Pro, open
              {' '}
              <strong>inkshare.app</strong>
              {' '}
              in your browser and upgrade there. Your account unlocks in the app straight away.
            </Alert>
          ) : (
            <button
              type="button"
              className="btn btn--accent btn--block"
              onClick={upgrade}
              disabled={busy || !status}
            >
              {busy ? 'Taking you to checkout…' : price ? `Go Pro — ${price} ${every}` : 'Go Pro'}
            </button>
          )}
          <p className="tiny muted center" style={{ marginTop: 10 }}>
            {status?.price && status.price.interval !== 'year'
              ? 'Cancel any time — you keep Pro until the period you paid for is up.'
              : 'Billed once a year. Cancel any time — you keep Pro until the year you paid for is up.'}
          </p>
        </>
      ) : null}
    </Modal>
  );
}

function PlanCard({
  plan, features, highlight = false, current = false, price = null,
}: {
  plan: { name: string };
  features: Array<{ label: string; has: boolean }>;
  highlight?: boolean;
  current?: boolean;
  /** Already formatted; absent when the server could not read it from Stripe. */
  price?: string | null;
}) {
  return (
    <div className={`plan${highlight ? ' plan--pro' : ''}`}>
      <div className="row row--between" style={{ marginBottom: 10 }}>
        <strong>{plan.name}</strong>
        {current ? <span className="pill-tag">Your plan</span> : null}
      </div>
      <div className="plan__price">{price ?? (highlight ? '' : 'Free')}</div>
      <ul className="plan__list">
        {features.map((f) => (
          <li key={f.label} className={f.has ? '' : 'plan__list-off'}>
            {f.has ? <IconCheck size={15} /> : <IconClose size={15} />}
            <span>{f.has ? f.label : '—'}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
